/**
 * En-tête de la fiche substance
 * Affiche l'identité complète: CAS, noms, formule, synonymes
 * Inclut le badge CMR (D6 Option C) et les pictogrammes GHS
 */
import { SubstanceFiche, CMRStatus, GHSPictogram } from '../../services/api'
import Badge from '../ui/Badge'
import { GHSPictogramList } from '../ghs/GHSPictogram'

interface SubstanceHeaderProps {
  identite: SubstanceFiche['identite']
  cmr?: CMRStatus | null
  pictogrammes?: GHSPictogram[]
}

export default function SubstanceHeader({ identite, cmr, pictogrammes }: SubstanceHeaderProps) {
  const isCMR = cmr?.est_cmr === true

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
      {/* Ligne principale: CAS + Nom + CMR Badge */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <Badge variant="primary" size="lg">
              CAS {identite.cas_number}
            </Badge>
            {/* Badge CMR dans le header (D6 Option C) - variantes uniformisées */}
            {isCMR && (
              <Badge
                variant={cmr?.categorie?.includes('1') ? 'cmr1' : 'cmr2'}
                size="lg"
              >
                CMR {cmr?.categorie || ''}
              </Badge>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {identite.nom_fr || identite.nom_en || 'Nom non disponible'}
          </h1>
          {identite.nom_en && identite.nom_fr && (
            <p className="text-lg text-gray-600 mt-1 italic">
              {identite.nom_en}
            </p>
          )}
        </div>

        {/* Formule et masse moléculaire */}
        <div className="flex flex-col items-start sm:items-end gap-2 text-right">
          {identite.formule_moleculaire && (
            <div>
              <span className="text-xs text-gray-500 uppercase tracking-wide">Formule</span>
              <p className="font-mono text-lg font-semibold text-gray-800">
                {identite.formule_moleculaire}
              </p>
            </div>
          )}
          {identite.masse_moleculaire && (
            <div>
              <span className="text-xs text-gray-500 uppercase tracking-wide">Masse mol.</span>
              <p className="font-mono text-gray-800">
                {identite.masse_moleculaire} g/mol
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Pictogrammes GHS */}
      {pictogrammes && pictogrammes.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <span className="text-xs text-gray-500 uppercase tracking-wide block mb-2">
            Pictogrammes de danger
          </span>
          <GHSPictogramList pictograms={pictogrammes} size="md" />
        </div>
      )}

      {/* Synonymes */}
      {identite.synonymes && identite.synonymes.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <span className="text-xs text-gray-500 uppercase tracking-wide block mb-2">
            Synonymes
          </span>
          <div className="flex flex-wrap gap-2">
            {identite.synonymes.slice(0, 8).map((synonyme, index) => (
              <span
                key={index}
                className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded"
              >
                {synonyme}
              </span>
            ))}
            {identite.synonymes.length > 8 && (
              <span className="inline-block px-2 py-1 text-gray-500 text-sm">
                +{identite.synonymes.length - 8} autres
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
